import { IBusStop } from "@/domain/entities/busStop";
import {
  IFavoriteLine,
  IFavoriteLineData,
} from "@/domain/entities/favoriteLine";
import {
  IBusLocation,
  ILineDistrict,
  ILineType,
  ILineInfo,
  ILineInfoLite,
  ILineFareInfo,
  IBookInterval,
  ITurn,
  IDownloadedFile,
  IContact,
  IFullSeatLine,
} from "@/domain/entities/lineInfo";
import {
  ArrivalTimeOptionCodeType,
  BookIntervalCode,
  LineTypeCode,
  SERVICE_UID,
} from "@/types";
import { Result, combine, ok } from "neverthrow";
import { IBaseParser, IBaseResponse } from "../shared/baseParser";
import { HttpError } from "../shared/errors/httpError";
import { ParseError } from "../shared/errors/parseError";
import { IHttpService } from "../shared/httpService";
import qs from "qs";
import {
  ILineInfoParser,
  IBusStopParser,
  IFavoriteLineParser,
  HttpLineInfoResponse,
  HttpLineInfoDetailResponse,
  HttpBusStopResponse,
  HttpFavoriteLineResponse,
  ILineInfoRepository,
  IBusLocationParser,
  HttpBusLocationResponse,
  HttpLineTypeResponse,
  ILineTypeParser,
  ILineDistrictParser,
  HttpLineDistrictResponse,
  ILineFareParser,
  HttpLineFareResponse,
  IBookIntervalParser,
  HttpBookIntervalResponse,
  HttpOnBoardLineResponse,
  IOnBoardLineParser,
  OptionMapper,
  IFilterParser,
  HttpTurnListResponse,
  ITurnParser,
  HttpBusStopLayoutInfoResponse,
  IBusStopLayoutInfoParser,
  IContactParser,
  HttpContactListResponse,
  GetOptions,
  ITmapArrivalTimeParser,
  HttpTmapArrivalTimeResponse,
  HttpLineTurnStationListResponse,
  ILineTurnBusStopParser,
  IFavoriteBusStopParser,
  HttpFavoriteBusStopListResponse,
  HttpRoadEventListResponse,
  IRoadEventsCollectionParser,
  HttpFullSeatLineListResponse,
  IFullSeatLineParser,
  HttpLineInfoLiteResponse,
  IBusStopLiteParser,
  ITurnLiteParser,
  ILineInfoLiteParser,
  HttpLineSearchFilterResponse,
  ILineSearchFilterParser,
} from "./lineInfoRepository.types";
import useGetAccessToken from "@/use/useGetAccessToken";
import { IResultData } from "@/domain/entities/shared/result";
import { inject, injectable } from "inversify";
import TYPES from "@/ioc/identifiers";
import useGetProtocolHost from "@/use/useGetProtocolHost";
import useGetServiceInfo from "@/use/useGetServiceInfo";
import { IOnBoardLineData } from "@/domain/entities/onBoardLine/onBoardLine.types";
import {
  FilterKeyValueType,
  IFilter,
  ILineSearchFilter,
  IOption,
} from "@/domain/entities/filter/filter.types";
import { IBusStopLayoutInfo } from "@/domain/entities/lineInfo/busStopLayout.types";
import { IFileDownloadParser } from "./parsers/fileDownloadParser";
import { IArrivalTime } from "@/domain/entities/lineInfo/arrivalTime.types";
import { IFavoriteBusStop } from "@/domain/entities/favoriteBusStop";
import { IRoadEventsCollection } from "@/domain/entities/roadEvent";

@injectable()
export class LineInfoRepository implements ILineInfoRepository {
  @inject(TYPES.ILineInfoParser)
  private lineInfoParser!: ILineInfoParser;

  @inject(TYPES.ITurnParser)
  private turnParser!: ITurnParser;

  @inject(TYPES.ILineTypeParser)
  private lineTypeParser!: ILineTypeParser;

  @inject(TYPES.ILineDistrictParser)
  private lineDistrictParser!: ILineDistrictParser;

  @inject(TYPES.IBusStopParser)
  private busStopParser!: IBusStopParser;

  @inject(TYPES.IFavoriteLineParser)
  private favoriteLineParser!: IFavoriteLineParser;

  @inject(TYPES.IBaseParser)
  private baseParser!: IBaseParser;

  @inject(TYPES.IBusLocationParser)
  private busLocationParser!: IBusLocationParser;

  @inject(TYPES.ILineFareParser)
  private lineFareParser!: ILineFareParser;

  @inject(TYPES.IBookIntervalParser)
  private bookIntervalParser!: IBookIntervalParser;

  @inject(TYPES.IOnBoardLineParser)
  private onBoardFavoriteLineParser!: IOnBoardLineParser;

  @inject(TYPES.IFilterParser)
  private filterParser!: IFilterParser;

  @inject(TYPES.IBusStopLayoutInfoParser)
  private busStopLayoutInfoParser!: IBusStopLayoutInfoParser;

  @inject(TYPES.IFileDownloadParser)
  private fileDownloadParser!: IFileDownloadParser;

  @inject(TYPES.IContactParser)
  private contactParser!: IContactParser;

  @inject(TYPES.ITmapArrivalTimeParser)
  private tmapArrivalTimeParser!: ITmapArrivalTimeParser;

  @inject(TYPES.ILineTurnBusStopParser)
  private lineTurnBusStopParser!: ILineTurnBusStopParser;

  @inject(TYPES.IFavoriteBusStopParser)
  private favoriteBusStopParser!: IFavoriteBusStopParser;

  @inject(TYPES.IRoadEventsCollectionParser)
  private roadEventsCollectionParser!: IRoadEventsCollectionParser;

  @inject(TYPES.IFullSeatLineParser)
  private fullSeatLineParser!: IFullSeatLineParser;

  @inject(TYPES.IBusStopLiteParser)
  private busStopLiteParser!: IBusStopLiteParser;

  @inject(TYPES.ITurnLiteParser)
  private turnLiteParser!: ITurnLiteParser;

  @inject(TYPES.ILineInfoLiteParser)
  private lineInfoLiteParser!: ILineInfoLiteParser;

  @inject(TYPES.ILineSearchFilterParser)
  private lineSearchFilterParser!: ILineSearchFilterParser;

  private httpService: IHttpService;

  private isPublicLineApi = useGetServiceInfo()?.isPublicLineApi ?? false;

  private publicPath = this.isPublicLineApi ? "/public" : "";

  constructor(@inject(TYPES.IHttpService) httpService: IHttpService) {
    this.httpService = httpService;
    this.httpService.initService(
      `${useGetProtocolHost()}/aibos/client/api/${
        process.env.VUE_APP_API_VERSION
      }${this.publicPath}/service/${SERVICE_UID}/line`
    );
  }

  async getAllLineInfosLite(
    sortOrder = "no asc,name asc"
  ): Promise<Result<ILineInfoLite[], ParseError | HttpError>> {
    const parseTo = (response: HttpLineInfoLiteResponse) => {
      const results = response.data.map(this.lineInfoLiteParser.toDomain);

      // 성공한 노선들만 필터링
      const successfulLines: ILineInfoLite[] = [];
      const failedLines: { data: any; error: string }[] = [];

      results.forEach((result, index) => {
        if (result.isOk()) {
          successfulLines.push(result.value);
        } else {
          failedLines.push({
            data: response.data[index],
            error: result.error.message,
          });
        }
      });

      // 실패한 노선들 로그 출력
      if (failedLines.length > 0) {
        console.warn(
          `[getAllLineInfosLite] ${failedLines.length}개 노선 파싱 실패:`,
          failedLines
        );
      }

      return ok(successfulLines);
    };

    return await this.httpService.get<
      HttpLineInfoLiteResponse,
      ILineInfoLite[]
    >(
      {
        url: `/route-lite?searchText=&sortOrder=${sortOrder}`,
        config: {
          headers: this.getHeaders(this.isPublicLineApi),
        },
      },
      { parseTo }
    );
  }

  async getAllLineInfosLiteByFilters(
    query: string,
    sortOrder = "no asc,name asc"
  ): Promise<Result<ILineInfoLite[], ParseError | HttpError>> {
    const parseTo = (response: HttpLineInfoLiteResponse) => {
      const results = response.data.map(this.lineInfoLiteParser.toDomain);

      // 성공한 노선들만 필터링
      const successfulLines: ILineInfoLite[] = [];
      const failedLines: { data: any; error: string }[] = [];

      results.forEach((result, index) => {
        if (result.isOk()) {
          successfulLines.push(result.value);
        } else {
          failedLines.push({
            data: response.data[index],
            error: result.error.message,
          });
        }
      });

      // 실패한 노선들 로그 출력
      if (failedLines.length > 0) {
        console.warn(
          `[getAllLineInfosLiteByFilters] ${failedLines.length}개 노선 파싱 실패:`,
          failedLines
        );
      }

      return ok(successfulLines);
    };

    return await this.httpService.get<
      HttpLineInfoLiteResponse,
      ILineInfoLite[]
    >(
      {
        url: `/route-lite?sortOrder=${sortOrder}&${query}`,
        config: {
          headers: this.getHeaders(this.isPublicLineApi),
        },
      },
      { parseTo }
    );
  }

  private getHeaders(
    isPublic: boolean,
    extraHeaders?: { [key: string]: string }
  ) {
    const token = useGetAccessToken();

    return isPublic
      ? extraHeaders
      : { ...extraHeaders, Authorization: `Bearer ${token}` };
  }

  async getAllLineInfos(
    sortOrder = "no asc,name asc"
  ): Promise<Result<ILineInfo[], ParseError | HttpError>> {
    const parseTo = (response: HttpLineInfoResponse) => {
      const results = response.data.map(this.lineInfoParser.toDomain);

      // 성공한 노선들만 필터링
      const successfulLines: ILineInfo[] = [];
      const failedLines: { data: any; error: string }[] = [];

      results.forEach((result, index) => {
        if (result.isOk()) {
          successfulLines.push(result.value);
        } else {
          failedLines.push({
            data: response.data[index],
            error: result.error.message,
          });
        }
      });

      // 실패한 노선들 로그 출력
      if (failedLines.length > 0) {
        console.warn(
          `[getAllLineInfos] ${failedLines.length}개 노선 파싱 실패:`,
          failedLines
        );
      }

      return ok(successfulLines);
    };

    return await this.httpService.get<HttpLineInfoResponse, ILineInfo[]>(
      {
        url: `/route?searchText=&sortOrder=${sortOrder}`,
        config: {
          headers: this.getHeaders(this.isPublicLineApi),
        },
      },
      { parseTo }
    );
  }

  async getLineInfosByFilters(
    query: string,
    sortOrder = "no asc,name asc"
  ): Promise<Result<ILineInfo[], ParseError | HttpError>> {
    const parseTo = (response: HttpLineInfoResponse) => {
      const results = response.data.map(this.lineInfoParser.toDomain);

      // 성공한 노선들만 필터링
      const successfulLines: ILineInfo[] = [];
      const failedLines: { data: any; error: string }[] = [];

      results.forEach((result, index) => {
        if (result.isOk()) {
          successfulLines.push(result.value);
        } else {
          failedLines.push({
            data: response.data[index],
            error: result.error.message,
          });
        }
      });

      // 실패한 노선들 로그 출력
      if (failedLines.length > 0) {
        console.warn(
          `[getLineInfosByFilters] ${failedLines.length}개 노선 파싱 실패:`,
          failedLines
        );
      }

      return ok(successfulLines);
    };

    return await this.httpService.get<HttpLineInfoResponse, ILineInfo[]>(
      {
        url: `/route?searchText=&sortOrder=${sortOrder}&${query}`,
        config: {
          headers: this.getHeaders(this.isPublicLineApi),
        },
      },
      { parseTo }
    );
  }

  async getLineInfoDetail(
    routeId: string
  ): Promise<Result<ILineInfo, ParseError | HttpError>> {
    const parseTo = (response: HttpLineInfoDetailResponse) => {
      return this.lineInfoParser.toDomain(response.data);
    };
    return await this.httpService.get<HttpLineInfoDetailResponse, ILineInfo>(
      {
        url: `/route/${routeId}`,
        config: {
          headers: this.getHeaders(this.isPublicLineApi),
        },
      },
      { parseTo }
    );
  }

  async getLineTypes(): Promise<Result<ILineType[], ParseError | HttpError>> {
    const parseTo = (response: HttpLineTypeResponse) => {
      const lineTypes = response.data.map(this.lineTypeParser.toDomain);
      return combine(lineTypes);
    };
    return await this.httpService.get<HttpLineTypeResponse, ILineType[]>(
      {
        url: "/line-type",
        config: {
          headers: this.getHeaders(this.isPublicLineApi),
        },
      },
      { parseTo }
    );
  }

  async getLineDistricts(
    lineTypeCode?: LineTypeCode
  ): Promise<Result<ILineDistrict[], ParseError | HttpError>> {
    const parseTo = (response: HttpLineDistrictResponse) => {
      const lineDistricts = response.data.map(this.lineDistrictParser.toDomain);
      return combine(lineDistricts);
    };
    return await this.httpService.get<
      HttpLineDistrictResponse,
      ILineDistrict[]
    >(
      {
        url: "/line-district",
        config: {
          headers: this.getHeaders(this.isPublicLineApi),
          params: {
            lineTypeCode,
          },
        },
      },
      { parseTo }
    );
  }

  async getLineTurns(
    searchParms: string
  ): Promise<Result<ITurn[], ParseError | HttpError>> {
    const parseTo = (response: HttpTurnListResponse) => {
      const turn = response.data.map(this.turnParser.toDomain);
      return combine(turn);
    };

    return await this.httpService.get<HttpTurnListResponse, ITurn[]>(
      {
        url: `/turn?${searchParms}`,
        config: {
          headers: this.getHeaders(this.isPublicLineApi),
        },
      },
      { parseTo }
    );
  }

  async getNearBusStops(
    latitude: number,
    longitude: number,
    radiusMeters: number
  ): Promise<Result<IBusStop[], ParseError | HttpError>> {
    const parseTo = (response: HttpBusStopResponse) => {
      const busStops = response.data.map(this.busStopParser.toDomain);
      return combine(busStops);
    };
    return await this.httpService.get<HttpBusStopResponse, IBusStop[]>(
      {
        url: "/station-by-distance",
        config: {
          headers: this.getHeaders(this.isPublicLineApi),
          params: {
            lat: latitude,
            lng: longitude,
            radiusM: radiusMeters,
          },
        },
      },
      { parseTo }
    );
  }

  async getFavoriteLineInfos(): Promise<
    Result<IFavoriteLine[], ParseError | HttpError>
  > {
    const parseTo = (response: HttpFavoriteLineResponse) => {
      const favoriteLines = response.data.map(this.favoriteLineParser.toDomain);
      return combine(favoriteLines);
    };

    return await this.httpService.get<
      HttpFavoriteLineResponse,
      IFavoriteLine[]
    >(
      {
        url: "/turn/favorite",
        config: {
          baseURL: `${useGetProtocolHost()}/aibos/client/api/${
            process.env.VUE_APP_API_VERSION
          }/service/${SERVICE_UID}/line`,
          headers: { Authorization: `Bearer ${useGetAccessToken()}` },
        },
      },
      { parseTo }
    );
  }

  async addFavoriteLine(
    favoriteLine: IFavoriteLineData,
    getOnTargetDate?: string,
    getOffTargetDate?: string
  ): Promise<Result<IResultData, ParseError | HttpError>> {
    const parseTo = (response: IBaseResponse) => {
      return this.baseParser.toDomain(response);
    };
    const data = {
      onStationUid: favoriteLine.getOnBusStop?.id,
      onNoticeYn: !!favoriteLine.getOnBusStop?.id,
      offStationUid: favoriteLine.getOffBusStop?.id,
      offNoticeYn: !!favoriteLine.getOffBusStop?.id,
      displayOrder: 1,
      onTargetDate: getOnTargetDate,
      offTargetDate: getOffTargetDate,
    };

    return await this.httpService.put<IBaseResponse, IResultData>(
      {
        url: `/turn/${favoriteLine.turnId}/favorite`,
        data: qs.stringify(data),
        config: {
          baseURL: `${useGetProtocolHost()}/aibos/client/api/${
            process.env.VUE_APP_API_VERSION
          }/service/${SERVICE_UID}/line`,
          headers: {
            Authorization: `Bearer ${useGetAccessToken()}`,
            "content-type": "application/x-www-form-urlencoded",
          },
        },
      },
      { parseTo }
    );
  }

  async removeFavoriteLine(
    turnId: string,
    onStationId?: string,
    offStationId?: string
  ): Promise<Result<IResultData, ParseError | HttpError>> {
    const parseTo = (response: IBaseResponse) => {
      return this.baseParser.toDomain(response);
    };

    let url = `/turn/${turnId}`;
    // if (onStationId) {
    //   url += `/on-station/${onStationId}`;
    // }
    // if (offStationId) {
    //   url += `/off-station/${offStationId}`;
    // }
    url += "/favorite";

    return await this.httpService.delete<IBaseResponse, IResultData>(
      {
        url,
        config: {
          baseURL: `${useGetProtocolHost()}/aibos/client/api/${
            process.env.VUE_APP_API_VERSION
          }/service/${SERVICE_UID}/line`,
          headers: {
            Authorization: `Bearer ${useGetAccessToken()}`,
          },
        },
      },
      { parseTo }
    );
  }

  async getBusLocations(
    routeId: string,
    turnId?: string,
    allocUid?: string
  ): Promise<Result<IBusLocation[], ParseError | HttpError>> {
    const parseTo = (response: HttpBusLocationResponse) => {
      const busLocations = response.data.map(this.busLocationParser.toDomain);
      return combine(busLocations);
    };
    const params = {
      lineRouteUid: routeId,
      lineTurnUid: turnId,
      allocUid,
    };

    return await this.httpService.get<HttpBusLocationResponse, IBusLocation[]>(
      {
        url: "/drive-location",
        config: {
          headers: this.getHeaders(this.isPublicLineApi),
          params,
        },
      },
      { parseTo }
    );
  }

  async getLineFares(
    turnId: string,
    bookIntervalCode?: BookIntervalCode
  ): Promise<Result<ILineFareInfo[], ParseError | HttpError>> {
    const parseTo = (response: HttpLineFareResponse) => {
      const lineFares = response.data.map(this.lineFareParser.toDomain);
      return combine(lineFares);
    };
    const params = {
      lineTurnUid: turnId,
      bookIntervalCode,
    };

    const publicPath = useGetServiceInfo().isPublicLineApi ? "/public" : "";

    return await this.httpService.get<HttpLineFareResponse, ILineFareInfo[]>(
      {
        url: "/fare-target",
        config: {
          headers: this.getHeaders(this.isPublicLineApi),
          baseURL: `${useGetProtocolHost()}/aibos/client/api/${
            process.env.VUE_APP_API_VERSION
          }${publicPath}/service/${SERVICE_UID}/book`,
          params,
        },
      },
      { parseTo }
    );
  }

  async getBookIntervals(
    turnId?: string
  ): Promise<Result<IBookInterval[], ParseError | HttpError>> {
    const parseTo = (response: HttpBookIntervalResponse) => {
      const bookIntervals = response.data.map(this.bookIntervalParser.toDomain);
      return combine(bookIntervals);
    };
    const params = {
      lineTurnUid: turnId,
    };

    return await this.httpService.get<
      HttpBookIntervalResponse,
      IBookInterval[]
    >(
      {
        url: "/book-interval",
        config: {
          baseURL: `${useGetProtocolHost()}/aibos/client/api/${
            process.env.VUE_APP_API_VERSION
          }/service/${SERVICE_UID}/book`,
          headers: { Authorization: `Bearer ${useGetAccessToken()}` },
          params,
        },
      },
      { parseTo }
    );
  }

  async getOnBoardLine(): Promise<
    Result<IOnBoardLineData, ParseError | HttpError>
  > {
    const parseTo = (response: HttpOnBoardLineResponse) => {
      return this.onBoardFavoriteLineParser.toDomain(response.data);
    };
    return await this.httpService.get<
      HttpOnBoardLineResponse,
      IOnBoardLineData
    >(
      {
        url: "/board/info",
        config: {
          baseURL: `${useGetProtocolHost()}/aibos/client/api/${
            process.env.VUE_APP_API_VERSION
          }/service/${SERVICE_UID}`,
          headers: { Authorization: `Bearer ${useGetAccessToken()}` },
        },
      },
      { parseTo }
    );
  }

  async getFilter<TResponse = any>(
    pathParam: string,
    key: string,
    type: FilterKeyValueType,
    optionMapper: OptionMapper<TResponse>,
    isPublic: boolean,
    multiple?: boolean,
    label?: string,
    dummyOptionName?: string,
    defaultOptionCode?: string,
    parentKey?: string,
    essential?: boolean,
    callback?: (selectedOptions?: IOption[] | IOption) => void,
    getOptions?: GetOptions<TResponse>,
    allowDirectInput = false
  ): Promise<Result<IFilter, ParseError | HttpError>> {
    const parseTo = (response: any) => {
      return this.filterParser.toDomain(
        response,
        pathParam,
        key,
        type,
        optionMapper,
        multiple,
        label,
        dummyOptionName,
        defaultOptionCode,
        parentKey,
        essential,
        callback,
        getOptions,
        allowDirectInput
      );
    };

    return await this.httpService.get<any, IFilter>(
      {
        url: pathParam,
        config: {
          baseURL: `${useGetProtocolHost()}/aibos/client/api/${
            process.env.VUE_APP_API_VERSION
          }`,
          headers: this.getHeaders(isPublic),
        },
      },
      { parseTo }
    );
  }

  async getBusStopLayoutInfo(
    turnUid: string,
    busStopUid: string
  ): Promise<Result<IBusStopLayoutInfo, ParseError | HttpError>> {
    const parseTo = (response: HttpBusStopLayoutInfoResponse) => {
      return this.busStopLayoutInfoParser.toDomain(response.data?.[0]);
    };

    return await this.httpService.get<
      HttpBusStopLayoutInfoResponse,
      IBusStopLayoutInfo
    >(
      {
        url: `/turn/${turnUid}/station-layout?lineStationUid=${busStopUid}`,
        config: {
          headers: { Authorization: `Bearer ${useGetAccessToken()}` },
        },
      },
      { parseTo }
    );
  }

  async downloadFile(
    fileUid: string
  ): Promise<Result<IDownloadedFile, ParseError | HttpError>> {
    const parseTo = (response: Blob) => {
      return this.fileDownloadParser.toDomain(response);
    };

    return await this.httpService.get<Blob, IDownloadedFile>(
      {
        url: `/download/${fileUid}`,
        config: {
          baseURL: `${useGetProtocolHost()}/aibos/client/api/${
            process.env.VUE_APP_API_VERSION
          }/service/${SERVICE_UID}/file`,
          headers: { Authorization: `Bearer ${useGetAccessToken()}` },
          responseType: "blob",
        },
      },
      { parseTo }
    );
  }

  async getContactList(): Promise<Result<IContact[], ParseError | HttpError>> {
    const parseTo = (response: HttpContactListResponse) => {
      const contacts = response.data.map(this.contactParser.toDomain);
      return combine(contacts);
    };

    return await this.httpService.get<HttpContactListResponse, IContact[]>(
      {
        url: "/contact",
        config: {
          baseURL: `${useGetProtocolHost()}/aibos/client/api/${
            process.env.VUE_APP_API_VERSION
          }/service/${SERVICE_UID}/line`,
          headers: { Authorization: `Bearer ${useGetAccessToken()}` },
        },
      },
      { parseTo }
    );
  }

  async createTmapMultipleViaPointsGuide(
    busLocation: IBusLocation
  ): Promise<Result<IArrivalTime[], ParseError | HttpError>> {
    const parseTo = (response: HttpTmapArrivalTimeResponse) => {
      return this.tmapArrivalTimeParser.toDomain(response.data);
    };

    const data = this.tmapArrivalTimeParser.fromDomain(busLocation);

    // 시작 정류장 + 경유지 30개 + 마지막 정류장 : 30개
    const url =
      busLocation.busStopEvents.length > 30
        ? "/route-sequential100"
        : "/route-sequential30";

    return await this.httpService.post<
      HttpTmapArrivalTimeResponse,
      IArrivalTime[]
    >(
      {
        url,
        data,
        config: {
          baseURL: `${useGetProtocolHost()}/aibos/client/api/${
            process.env.VUE_APP_API_VERSION
          }/external/tmap`,
          headers: {
            Authorization: `Bearer ${useGetAccessToken()}`,
          },
        },
      },
      { parseTo }
    );
  }

  /** 과거도착시각, 평균도착시각 받아오는 API */
  async getBusStopArrivalTimes(
    lineTurnUid: string,
    busOrder: number,
    driveDate: string,
    type: ArrivalTimeOptionCodeType
  ): Promise<Result<IArrivalTime[], ParseError | HttpError>> {
    const parseTo = (response: HttpLineTurnStationListResponse) => {
      return combine(
        response.data.map((timeData) =>
          this.lineTurnBusStopParser.toDomain(timeData, type)
        )
      );
    };

    return await this.httpService.get<
      HttpLineTurnStationListResponse,
      IArrivalTime[]
    >(
      {
        url: `/turn/${lineTurnUid}/station?driveDate=${driveDate}&busOrder=${busOrder}`,
        config: {
          headers: {
            Authorization: `Bearer ${useGetAccessToken()}`,
          },
        },
      },
      { parseTo }
    );
  }

  async getFavoriteBusStops(): Promise<
    Result<IFavoriteBusStop[], ParseError | HttpError>
  > {
    const parseTo = (response: HttpFavoriteBusStopListResponse) => {
      const favoriteLines = response.data.map(
        this.favoriteBusStopParser.toDomain
      );
      return combine(favoriteLines);
    };

    return await this.httpService.get<
      HttpFavoriteBusStopListResponse,
      IFavoriteBusStop[]
    >(
      {
        url: "/favorite/station",
        config: {
          baseURL: `${useGetProtocolHost()}/aibos/client/api/${
            process.env.VUE_APP_API_VERSION
          }/service/${SERVICE_UID}`,
          headers: { Authorization: `Bearer ${useGetAccessToken()}` },
        },
      },
      { parseTo }
    );
  }

  async addFavoriteBusStop(
    favoriteBusStop: IFavoriteBusStop
  ): Promise<Result<IResultData, ParseError | HttpError>> {
    const parseTo = (response: IBaseResponse) => {
      return this.baseParser.toDomain(response);
    };

    const data = this.favoriteBusStopParser.fromDomain(favoriteBusStop);

    return await this.httpService.put<IBaseResponse, IResultData>(
      {
        url: "/favorite/station",
        data: qs.stringify(data),
        config: {
          baseURL: `${useGetProtocolHost()}/aibos/client/api/${
            process.env.VUE_APP_API_VERSION
          }/service/${SERVICE_UID}`,
          headers: {
            Authorization: `Bearer ${useGetAccessToken()}`,
            "content-type": "application/x-www-form-urlencoded",
          },
        },
      },
      { parseTo }
    );
  }

  async removeFavoriteBusStop(
    stationUid: string
  ): Promise<Result<IResultData, ParseError | HttpError>> {
    const parseTo = (response: IBaseResponse) => {
      return this.baseParser.toDomain(response);
    };
    return await this.httpService.delete<IBaseResponse, IResultData>(
      {
        url: `/favorite/station/${stationUid}`,
        config: {
          baseURL: `${useGetProtocolHost()}/aibos/client/api/${
            process.env.VUE_APP_API_VERSION
          }/service/${SERVICE_UID}`,
          headers: {
            Authorization: `Bearer ${useGetAccessToken()}`,
          },
        },
      },
      { parseTo }
    );
  }

  /** 교통정보 목록 조회 - 전체 조회시 offset, limit 없이 조회할것 */
  async getRoadEventList(
    routeUid: string,
    offset?: number,
    limit?: number
  ): Promise<Result<IRoadEventsCollection, ParseError | HttpError>> {
    const parseTo = (response: HttpRoadEventListResponse) => {
      return this.roadEventsCollectionParser.toDomain(response);
    };

    return await this.httpService.get<
      HttpRoadEventListResponse,
      IRoadEventsCollection
    >(
      {
        url: `/route/${routeUid}/road-event`,
        config: {
          params: {
            offset,
            limit,
          },
          headers: this.getHeaders(this.isPublicLineApi),
        },
      },
      { parseTo }
    );
  }

  /** 만석 노선 목록 조회 */
  async getFullSeatLineList(): Promise<
    Result<IFullSeatLine[], ParseError | HttpError>
  > {
    const parseTo = (response: HttpFullSeatLineListResponse) => {
      return combine(response.data.map(this.fullSeatLineParser.toDomain));
    };

    return await this.httpService.get<
      HttpFullSeatLineListResponse,
      IFullSeatLine[]
    >(
      {
        url: "/route/seat-full",
        config: {
          headers: {
            Authorization: `Bearer ${useGetAccessToken()}`,
          },
        },
      },
      { parseTo }
    );
  }

  /** 노선검색필터 조회 */
  async getLineSearchFilter(
    searchParams: string
  ): Promise<Result<ILineSearchFilter[], ParseError | HttpError>> {
    const parseTo = (response: HttpLineSearchFilterResponse) => {
      return combine(response.data.map(this.lineSearchFilterParser.toDomain));
    };

    return await this.httpService.get<
      HttpLineSearchFilterResponse,
      ILineSearchFilter[]
    >(
      {
        url: `/filter${searchParams}`,
        config: {
          headers: {
            Authorization: `Bearer ${useGetAccessToken()}`,
          },
        },
      },
      {
        parseTo,
      }
    );
  }
}
